import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  Alert,
  Fade,
  IconButton,
} from '@mui/material';
import {
  Email as EmailIcon,
  VerifiedUser as VerifiedUserIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useMutation } from '@apollo/client';
import { VERIFY_USER } from '../graphql/verifyUser';
import { RESEND_VERIFICATION_EMAIL } from '../graphql/resendVerificationEmail';
import { GET_ME } from '../graphql/getMe';
interface VerificationModalProps {
  open: boolean;
  onClose: () => void;
  email: string;
  onVerificationSuccess: () => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({
  open,
  onClose,
  email,
  onVerificationSuccess,
}) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [verifyUser, { loading: verifying }] = useMutation(VERIFY_USER, {
    refetchQueries: [GET_ME],
  });
  const [resendVerificationEmail, { loading: resending }] = useMutation(
    RESEND_VERIFICATION_EMAIL,
  );

  const handleVerify = async () => {
    try {
      const { data } = await verifyUser({
        variables: {
          email,
          code: verificationCode,
        },
      });

      if (data.verifyUser.success) {
        onVerificationSuccess();
        onClose();
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    }
  };

  const handleResend = async () => {
    try {
      await resendVerificationEmail({
        variables: { email },
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      hideBackdrop={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          overflow: 'hidden',
        },
      }}
    >
      <Fade in={open} timeout={500}>
        <Box>
          {/* Header */}
          <DialogTitle
            sx={{
              textAlign: 'center',
              pb: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: '12px 12px 0 0',
              fontWeight: 600,
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                pointerEvents: 'none',
              },
            }}
          >
            <EmailIcon sx={{ fontSize: '1.5rem' }} />
            Verify Your Email
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Main content with icon */}
              <Box sx={{ textAlign: 'center' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    background:
                      'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                    border: '1px solid rgba(102, 126, 234, 0.1)',
                  }}
                >
                  <VerifiedUserIcon
                    sx={{
                      fontSize: '3rem',
                      color: '#667eea',
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#2c3e50',
                      mb: 1,
                    }}
                  >
                    Check Your Email
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      lineHeight: 1.6,
                    }}
                  >
                    We've sent a verification code to{' '}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 600,
                        color: '#667eea',
                        backgroundColor: 'rgba(102, 126, 234, 0.1)',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {email}
                    </Box>
                  </Typography>
                </Paper>
              </Box>

              {/* Verification Code Input */}
              <Box>
                <TextField
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  error={!!error}
                  helperText={error}
                  fullWidth
                  variant="outlined"
                  placeholder="Enter verification code"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      '& fieldset': {
                        borderColor: error
                          ? '#f44336'
                          : 'rgba(102, 126, 234, 0.3)',
                        borderWidth: 2,
                      },
                      '&:hover fieldset': {
                        borderColor: error
                          ? '#f44336'
                          : 'rgba(102, 126, 234, 0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: error ? '#f44336' : '#667eea',
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      color: '#5a6c7d',
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    },
                  }}
                />
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  icon={<ErrorIcon />}
                  sx={{
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontSize: '0.9rem',
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  pt: 1.5,
                  borderTop: '1px solid rgba(102, 126, 234, 0.2)',
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleVerify}
                  disabled={verifying || !verificationCode}
                  fullWidth
                  size="large"
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background:
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background:
                        'linear-gradient(135deg, #b0b0b0 0%, #999999 100%)',
                      boxShadow: 'none',
                      transform: 'none',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {verifying ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                      <Typography>Verifying...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: '1.2rem' }} />
                      Verify Email
                    </Box>
                  )}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleResend}
                  disabled={resending}
                  fullWidth
                  size="medium"
                  sx={{
                    py: 1,
                    borderRadius: 2,
                    color: '#667eea',
                    borderColor: '#667eea',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#5a6fd8',
                      color: '#5a6fd8',
                      backgroundColor: 'rgba(102, 126, 234, 0.05)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      color: '#b0b0b0',
                      borderColor: '#b0b0b0',
                      backgroundColor: 'transparent',
                      transform: 'none',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {resending ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: '#667eea' }} />
                      <Typography>Sending...</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <RefreshIcon sx={{ fontSize: '1rem' }} />
                      Resend Verification Code
                    </Box>
                  )}
                </Button>
              </Box>
            </Stack>
          </DialogContent>
        </Box>
      </Fade>
    </Dialog>
  );
};

export default VerificationModal;
